import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRowAction, type KstDataGridRows } from '@/components/KstDataGrid';
import { Dropdownlist } from '@/components/Dropdownlist';
import { renderReactComponent, destroyReactComponent } from '@/react-bridge';
import { navigateToCallCard } from '@/services/navigation';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { getOfficerLabel, hasStringValue } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';

// ── Global Declarations ──────────────────────────────────────────────

import { getOfficerPluralLabel, getAvailableSubmodules } from '@/utils/auth';
import { isInArray } from '@/utils/arrays';
import { hasNumericValue } from '@/utils/validation';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const hasReportApproval = _wg('hasReportApproval');
const timeSince = _wg('timeSince');
const timeSinceFormatted = _wg('timeSinceFormatted');
const getOfficerStatusPromise = _wg('getOfficerStatusPromise');
const getCompanyVehicleListForOfficerStatusPromise = _wg('getCompanyVehicleListForOfficerStatusPromise');
const processOfficerStatusListFromResult = _wg('processOfficerStatusListFromResult');
const getGridBackupUnitClassNameFromOfficerStatus = _wg('getGridBackupUnitClassNameFromOfficerStatus');
const kstDataGridComponent = _wg('kstDataGridComponent');

// ── Types ────────────────────────────────────────────────────────────

interface OfficerStatusScreenProps {
  moduleType: 'cad' | 'mdt';
}

interface DispatcherEntry {
  name: string;
  company: string;
}

// ── HTML Template Functions ──────────────────────────────────────────
// These return HTML strings since KstDataGrid uses dangerouslySetInnerHTML

function getOnSceneTimeTemplate(e: any): string {
  if (e.onSceneTime) {
    const timeDiffMilliseconds = timeSince(e.onSceneTime);
    const timeDiffMinutes = (timeDiffMilliseconds / 1000.0) / 60.0;
    const formattedTimeDiff = timeSinceFormatted(new Date(e.onSceneTime));

    if (timeDiffMinutes < 5) {
      return `<span class="label label-StatusGreen">${formattedTimeDiff}</span>`;
    } else if (timeDiffMinutes < 15) {
      return `<span class="label label-StatusYellow">${formattedTimeDiff}</span>`;
    } else if (timeDiffMinutes < 30) {
      return `<span class="label label-StatusOrange">${formattedTimeDiff}</span>`;
    } else if (timeDiffMinutes < 45) {
      return `<span class="label label-StatusRed">${formattedTimeDiff}</span>`;
    } else {
      return `<span class="label label-StatusRed blink_me">${formattedTimeDiff}</span>`;
    }
  }
  return 'n/a';
}

function getActiveCallTemplate(e: any, moduleType: string): string {
  if (e.activeCall) {
    return `<a href="#" class="active-call-link">${e.activeCall.display}</a>`;
  }
  return 'n/a';
}

function getStatusLocationTemplate(e: any): string {
  if (hasNumericValue(e.statusLatitude) && hasNumericValue(e.statusLongitude)) {
    return `<a href="#" class="status-location-link" title="${e.statusLatitude}, ${e.statusLongitude}"><i class="fas fa-map-marker-alt"></i></a>`;
  }
  return '';
}

// ── Column & Row Action Builders ─────────────────────────────────────

function buildOfficerStatusGridColumns(moduleType: string): KstDataGridColumn[] {
  return [
    {
      field: 'officerCallSign',
      name: 'Call Sign',
      sortable: true,
      sortAccessor: (e: any) => {
        const callSign = (e.officerCallSign ?? '').toString().trim();
        return callSign || null;
      },
      secondarySortAccessor: (e: any) => e.officerName ?? '',
    },
    { field: 'officerName', name: getOfficerLabel(), sortable: true },
    { field: 'status', name: 'Call Status', sortable: true },
    { field: 'lastActiveTime', name: 'Active', sortable: true },
    {
      field: 'onSceneTime',
      name: 'Scene',
      sortable: true,
      template: (e: any) => getOnSceneTimeTemplate(e),
    },
    {
      field: 'activeCall',
      name: 'Call',
      sortable: true,
      sortAccessor: (e: any) => e.activeCall?.display ?? '',
      template: (e: any) => getActiveCallTemplate(e, moduleType),
    },
    { field: 'location', name: 'Location', sortable: true },
  ];
}

function buildRowActions(showSafetyCheck: boolean): KstDataGridRowAction[] {
  if (!showSafetyCheck) {
    return [];
  }
  return [
    {
      name: 'CHECKED',
      icon: undefined,
      event: 'safetycheck',
      class: 'btn-warning',
    },
  ];
}

// ── Vehicle Dropdown Wrapper ─────────────────────────────────────────
// A small component rendered into each grid cell container via the bridge

interface VehicleDropdownCellProps {
  officerId: number;
  currentVehicleId: number | null;
  vehicleItems: { id: number | null; name: string }[];
  onVehicleChange: (officerId: number, value: number | null) => void;
}

function VehicleDropdownCell({
  officerId,
  currentVehicleId,
  vehicleItems,
  onVehicleChange,
}: VehicleDropdownCellProps) {
  const [selectedValue, setSelectedValue] = useState<string | number | null>(currentVehicleId);

  // The cell instance survives the 60s data refresh (stable row keys), so
  // pick up assignments made elsewhere when the fetched value changes.
  useEffect(() => {
    setSelectedValue(currentVehicleId);
  }, [currentVehicleId]);

  const handleChange = useCallback(
    (newValue: string | number | null) => {
      setSelectedValue(newValue);
      // Parse value to number or null
      let parsedVehicleId: number | null = null;
      if (newValue !== null && newValue !== '' && newValue !== undefined) {
        const rawId = typeof newValue === 'object' && newValue !== null ? (newValue as any).id : newValue;
        if (rawId !== null && rawId !== undefined) {
          const parsed = typeof rawId === 'number' ? rawId : parseInt(String(rawId), 10);
          if (!isNaN(parsed)) {
            parsedVehicleId = parsed;
          }
        }
      }
      onVehicleChange(officerId, parsedVehicleId);
    },
    [officerId, onVehicleChange]
  );

  return (
    <Dropdownlist
      value={selectedValue}
      items={vehicleItems}
      labelKey="name"
      valueKey="id"
      placeholder="Select vehicle..."
      searchable={true}
      clearable={false}
      sortItems={false}
      onChange={handleChange}
    />
  );
}

// ── Component ────────────────────────────────────────────────────────

export function OfficerStatusScreen({ moduleType }: OfficerStatusScreenProps) {
  const { theme } = useTheme();

  // Labels
  const officerLabel = useMemo(() => getOfficerLabel(), []);
  const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);

  // Permissions
  const showDispatchers = moduleType === 'cad';
  const showSafetyCheck = moduleType === 'cad' || hasReportApproval();

  // State: grids
  const [isOnlineLoading, setIsOnlineLoading] = useState(false);
  const [isOfflineLoading, setIsOfflineLoading] = useState(false);
  const [onlineGridRows, setOnlineGridRows] = useState<KstDataGridRows>({ entries: null });
  const [offlineGridRows, setOfflineGridRows] = useState<KstDataGridRows>({ entries: null });

  // State: columns (may get vehicle column appended)
  const [onlineGridColumns, setOnlineGridColumns] = useState<KstDataGridColumn[]>(
    () => buildOfficerStatusGridColumns(moduleType)
  );
  const [offlineGridColumns, setOfflineGridColumns] = useState<KstDataGridColumn[]>(
    () => buildOfficerStatusGridColumns(moduleType)
  );

  // Row actions
  const rowActions = useMemo(() => buildRowActions(showSafetyCheck), [showSafetyCheck]);

  // State: sort (per grid)
  const [onlineSortField, setOnlineSortField] = useState<string>('officerCallSign');
  const [onlineSortDir, setOnlineSortDir] = useState<string>('asc');
  const [offlineSortField, setOfflineSortField] = useState<string>('officerCallSign');
  const [offlineSortDir, setOfflineSortDir] = useState<string>('asc');

  const onOnlineSort = useCallback((field: string) => {
    if (onlineSortField === field) {
      setOnlineSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setOnlineSortField(field);
      setOnlineSortDir('asc');
    }
  }, [onlineSortField]);

  const onOfflineSort = useCallback((field: string) => {
    if (offlineSortField === field) {
      setOfflineSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setOfflineSortField(field);
      setOfflineSortDir('asc');
    }
  }, [offlineSortField]);

  // State: vehicles. Kept in a ref so the vehicle column's reactTemplate
  // (created once) reads the latest list on every grid render.
  const companyVehicleItemsRef = useRef<{ id: number | null; name: string }[]>([]);
  const vehicleColumnAddedRef = useRef(false);
  const statusUpdateColumnsAddedRef = useRef(false);

  // State: dispatchers (CAD only)
  const [dispatchers, setDispatchers] = useState<DispatcherEntry[]>([]);

  // Ref for auto-refresh interval
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Vehicle Permission Check ─────────────────────────────────────

  const checkVehiclePermission = useCallback((): boolean => {
    const submodules = getAvailableSubmodules() || [];
    return isInArray(submodules, AccessAuthorities.ROLE_ASSIGN_OFFICER_TO_COMPANY_VEHICLE);
  }, []);

  // ── Vehicle Change Handler ───────────────────────────────────────

  const onVehicleChange = useCallback((officerId: number, parsedVehicleId: number | null) => {
    api.assignOfficerToVehicle(
      { officerId, companyVehicleId: parsedVehicleId },
      () => { },
      () => {
        App.showPopupNotification(
          `Error assigning ${getOfficerLabel().toLowerCase()} to vehicle`,
          'error'
        );
      }
    );
  }, []);

  // ── Load Officer Status ──────────────────────────────────────────

  const loadOfficerStatus = useCallback(
    (authTokenRefresh: boolean) => {
      setIsOnlineLoading(true);
      setIsOfflineLoading(true);

      Promise.all([
        getOfficerStatusPromise(authTokenRefresh),
        getCompanyVehicleListForOfficerStatusPromise(authTokenRefresh),
      ])
        .then((values) => {
          const officerStatusResult = values[0] as any;
          const companyVehicleResult = values[1] as any;

          processOfficerStatusListFromResult(officerStatusResult);

          // Add status update columns if enabled and not already added
          if (officerStatusResult.isStatusUpdateEnabled && !statusUpdateColumnsAddedRef.current) {
            statusUpdateColumnsAddedRef.current = true;
            const statusUpdateColumns: KstDataGridColumn[] = [
              { field: 'statusComment', name: 'Status', sortable: true },
              { field: 'statusLatitude', name: '', width: '40px', template: (e: any) => getStatusLocationTemplate(e) },
            ];
            setOnlineGridColumns((prev) => [...prev, ...statusUpdateColumns]);
            setOfflineGridColumns((prev) => [...prev, ...statusUpdateColumns]);
          }

          const vehicleList = companyVehicleResult?.companyVehicles || [];
          companyVehicleItemsRef.current = [
            { id: null, name: 'None' },
            ...vehicleList.map((v: any) => ({
              id: v.id,
              name: v.vehicleName || v.name || '',
            })),
          ];

          // Add vehicle column if permitted and not already added
          if (checkVehiclePermission() && vehicleList.length > 0 && !vehicleColumnAddedRef.current) {
            vehicleColumnAddedRef.current = true;
            const vehicleColumn: KstDataGridColumn = {
              field: 'companyVehicleId',
              name: 'Vehicle',
              reactTemplate: (e: any) => (
                <VehicleDropdownCell
                  officerId={e.officerId}
                  currentVehicleId={e.companyVehicleId ?? null}
                  vehicleItems={companyVehicleItemsRef.current}
                  onVehicleChange={onVehicleChange}
                />
              ),
            };
            setOnlineGridColumns((prev) => [...prev, vehicleColumn]);
            setOfflineGridColumns((prev) => [...prev, { ...vehicleColumn }]);
          }

          // Set stable id so KstDataGrid keeps row DOM (and mounted vehicle dropdowns)
          // through sort reorders.
          officerStatusResult.officerStatusList.forEach((o: any) => {
            o.id = o.officerId;
          });

          // Split into online/offline
          const onlineOfficers = officerStatusResult.officerStatusList.filter(
            (o: any) => o.isOnline === true
          );
          const offlineOfficers = officerStatusResult.officerStatusList.filter(
            (o: any) => o.isOnline === false
          );

          setOnlineGridRows({ entries: onlineOfficers });
          setOfflineGridRows({ entries: offlineOfficers });

          setIsOnlineLoading(false);
          setIsOfflineLoading(false);
        })
        .catch(() => {
          setIsOnlineLoading(false);
          setIsOfflineLoading(false);
        });
    },
    [checkVehiclePermission, onVehicleChange]
  );

  // ── Load Dispatchers (CAD only) ──────────────────────────────────

  const loadDispatchers = useCallback((authTokenRefresh: boolean) => {
    api.getAvailableDispatchers(authTokenRefresh, (result: any) => {
      const mapped = result.dispatchersAvailableList.map((d: any) => {
        const company = result.companyList.find(
          (c: any) => c.id === d.companyId
        );
        return {
          name: d.dispatcherName,
          company: company ? company.name : '',
        };
      });
      setDispatchers(mapped);
    });
  }, []);

  // ── Safety Check ─────────────────────────────────────────────────

  const performSafetyCheck = useCallback(
    (officerId: number) => {
      api.securityCheck(String(officerId), () => {
        loadOfficerStatus(true);
      });
    },
    [loadOfficerStatus]
  );

  // ── Row Action Handlers ──────────────────────────────────────────

  const onOnlineRowAction = useCallback(
    (event: { action: string; row: any }) => {
      if (event.action === 'safetycheck') {
        performSafetyCheck(event.row.officerId);
      }
    },
    [performSafetyCheck]
  );

  const onOfflineRowAction = useCallback(
    (event: { action: string; row: any }) => {
      if (event.action === 'safetycheck') {
        performSafetyCheck(event.row.officerId);
      }
    },
    [performSafetyCheck]
  );

  // ── Cell Click (Active Call Navigation) ──────────────────────────

  const onCellClick = useCallback(
    (event: { row: any; column: any; field: string }) => {
      if (event.field === 'activeCall' && event.row.activeCall) {
        const callCardId = event.row.activeCall.id;
        navigateToCallCard(callCardId);
      }
      if (event.field === 'statusLatitude' && hasNumericValue(event.row.statusLatitude) && hasNumericValue(event.row.statusLongitude)) {
        window.open(`https://www.google.com/maps?q=${event.row.statusLatitude},${event.row.statusLongitude}`, '_blank');
      }
    },
    []
  );

  // ── Mount: Initial Load + Auto-Refresh ───────────────────────────

  useEffect(() => {
    // Initial data load
    loadOfficerStatus(true);

    if (showDispatchers) {
      loadDispatchers(true);
    }

    // Auto-refresh every 60 seconds
    refreshIntervalRef.current = setInterval(() => {
      loadOfficerStatus(false);
      if (showDispatchers) {
        loadDispatchers(false);
      }
    }, 60000);

    // Refresh when a status update is made from the Status modal
    const onStatusUpdated = () => loadOfficerStatus(false);
    window.addEventListener('orna:status-updated', onStatusUpdated);

    // Cleanup on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      window.removeEventListener('orna:status-updated', onStatusUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className={`officer-status-page ${theme}`.trim()}>
      {/* Dispatcher Sidebar (CAD only) */}
      {showDispatchers && (
        <aside className="officer-status-sidebar">
          <div className="dispatcher-panel">
            <div className="dispatcher-panel-header">
              <h2>Available Dispatchers</h2>
            </div>
            <div className="dispatcher-panel-body">
              {dispatchers.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th>{officerLabel}</th>
                      <th>Company</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispatchers.map((dispatcher) => (
                      <tr key={dispatcher.name}>
                        <td>{dispatcher.name}</td>
                        <td>{dispatcher.company}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="dispatcher-panel-empty">
                  No dispatchers available
                </div>
              )}
            </div>
          </div>
        </aside>
      )}

      {/* Main Content */}
      <main className="officer-status-main">
        <div className="officer-status-grids">
          {/* Online Officers Grid */}
          <div className="officer-status-grid-wrapper">
            <KstDataGrid
              title={`Online ${officerPluralLabel}`}
              columns={onlineGridColumns}
              rows={onlineGridRows}
              loading={isOnlineLoading}
              showcheckbox={false}
              showheader={true}
              mobiletitlefield="officerCallSign"
              expandable={true}
              rowactions={rowActions}
              mobileactionsonly={false}
              sortfield={onlineSortField}
              sortdirection={onlineSortDir}
              onSort={onOnlineSort}
              onRowAction={onOnlineRowAction}
              onCellClick={onCellClick}
            />
          </div>

          {/* Offline Officers Grid */}
          <div className="officer-status-grid-wrapper">
            <KstDataGrid
              title={`Offline ${officerPluralLabel}`}
              columns={offlineGridColumns}
              rows={offlineGridRows}
              loading={isOfflineLoading}
              showcheckbox={false}
              showheader={true}
              mobiletitlefield="officerCallSign"
              expandable={true}
              rowactions={rowActions}
              mobileactionsonly={false}
              sortfield={offlineSortField}
              sortdirection={offlineSortDir}
              onSort={onOfflineSort}
              onRowAction={onOfflineRowAction}
              onCellClick={onCellClick}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

// ── Imperative API (bridges legacy non-React code) ──────────────────

let officerStatusContainer: HTMLElement | null = null;

/**
 * Mount the OfficerStatusScreen React component into the appropriate container element.
 */
function setUpOfficerStatusScreen(moduleType: string): void {
  // Tear down any previous instance
  cleanUpOfficerStatus();

  const containerId =
    moduleType === 'cad' ? 'vueOfficerStatus' : 'vueMdtOfficerStatus';
  const el = document.getElementById(containerId);
  if (!el) return;

  officerStatusContainer = el;

  renderReactComponent(
    OfficerStatusScreen,
    { moduleType: moduleType as 'cad' | 'mdt' },
    officerStatusContainer
  );
}

/** Unmount the OfficerStatusScreen and clean up resources. */
function cleanUpOfficerStatus(): void {
  if (officerStatusContainer) {
    destroyReactComponent(officerStatusContainer);
    officerStatusContainer = null;
  }
}

// ── Window Registration ─────────────────────────────────────────────

(window as any).setUpOfficerStatusScreen = setUpOfficerStatusScreen;
(window as any).setUpOfficerStatusVue = setUpOfficerStatusScreen; // legacy alias
(window as any).cleanUpOfficerStatus = cleanUpOfficerStatus;

// ── Screen Registry ─────────────────────────────────────────────────


// ── Exports ─────────────────────────────────────────────────────────

export { setUpOfficerStatusScreen, cleanUpOfficerStatus };
